import { useState, useEffect, useCallback, useRef } from 'react';
import YAML from 'yaml';
import { usePyodide } from './usePyodide';

// Debounce delay for validation (ms)
const VALIDATION_DEBOUNCE_MS = 500;

// Valid model providers (for quick JS validation)
const VALID_PROVIDERS = ['openai', 'anthropic', 'google', 'azure', 'local', 'lmstudio', 'openrouter'];

// Required fields by content type (for quick JS validation)
const REQUIRED_FIELDS = {
  agent: ['name', 'model'],
  skill: ['name', 'description'],
  prompt: ['name'],
  judge: ['name', 'model'],
};

/**
 * Parse frontmatter from markdown content
 * @param {string} content - Markdown content with YAML frontmatter
 * @returns {{ frontmatter: object | null, body: string, error: object | null }}
 */
function parseFrontmatter(content) {
  if (!content || !content.startsWith('---')) {
    return {
      frontmatter: null,
      body: content,
      error: { line: 1, column: 1, message: 'Missing frontmatter delimiter (---)', source: 'syntax' },
    };
  }

  const lines = content.split('\n');
  let endIndex = -1;

  // Find the closing ---
  for (let i = 1; i < lines.length; i++) {
    if (lines[i].trim() === '---') {
      endIndex = i;
      break;
    }
  }

  if (endIndex === -1) {
    return {
      frontmatter: null,
      body: content,
      error: { line: 1, column: 1, message: 'Incomplete frontmatter - missing closing delimiter (---)', source: 'syntax' },
    };
  }

  const frontmatterText = lines.slice(1, endIndex).join('\n');
  const body = lines.slice(endIndex + 1).join('\n');

  try {
    // Parse YAML with custom handling for custom tags
    const frontmatter = YAML.parse(frontmatterText, {
      customTags: [
        // Handle !prompt, !agent, !graph tags
        {
          tag: '!prompt',
          resolve: (str) => ({ type: 'prompt', path: str }),
        },
        {
          tag: '!agent',
          resolve: (str) => ({ type: 'agent', path: str }),
        },
        {
          tag: '!graph',
          resolve: (str) => ({ type: 'graph', path: str }),
        },
      ],
    });

    return { frontmatter, body, error: null };
  } catch (e) {
    // Extract line number from YAML error
    const lineMatch = e.message.match(/at line (\d+)/);
    const line = lineMatch ? parseInt(lineMatch[1], 10) + 1 : 2; // +1 for the opening ---

    return {
      frontmatter: null,
      body,
      error: {
        line,
        column: 1,
        message: `YAML syntax error: ${e.message}`,
        source: 'syntax',
      },
    };
  }
}

/**
 * Quick JavaScript validation for immediate feedback
 * Runs synchronously and provides fast basic checks
 * @param {object} frontmatter - Parsed frontmatter
 * @param {string} contentType - Content type (agent, skill, prompt)
 * @returns {Array<{ line: number, column: number, message: string, severity?: string, source: string }>}
 */
function quickValidateFrontmatter(frontmatter, contentType) {
  const errors = [];

  if (!frontmatter) {
    return errors;
  }

  // Check required fields
  const requiredFields = REQUIRED_FIELDS[contentType] || [];
  for (const field of requiredFields) {
    if (!(field in frontmatter) || frontmatter[field] === null || frontmatter[field] === '') {
      errors.push({
        line: 2,
        column: 1,
        message: `Missing required field: '${field}'`,
        severity: 'error',
        source: 'syntax',
      });
    }
  }

  // Validate model format for agents and judges (quick check)
  if ((contentType === 'agent' || contentType === 'judge') && frontmatter.model) {
    const model = typeof frontmatter.model === 'object' ? frontmatter.model.name : frontmatter.model;

    if (model && typeof model === 'string') {
      // Skip template variables
      if (!model.startsWith('${')) {
        if (!model.includes(':')) {
          errors.push({
            line: 2,
            column: 1,
            message: `Invalid model format: '${model}'. Expected 'provider:model-name'`,
            severity: 'error',
            source: 'syntax',
          });
        } else {
          const provider = model.split(':')[0].toLowerCase();
          if (!VALID_PROVIDERS.includes(provider) && !provider.startsWith('http')) {
            errors.push({
              line: 2,
              column: 1,
              message: `Unknown provider: '${provider}'. Valid providers: ${VALID_PROVIDERS.join(', ')}`,
              severity: 'warning',
              source: 'syntax',
            });
          }
        }
      }
    }
  }

  // Validate array fields
  const arrayFields = ['skills', 'mcp_servers', 'tools'];
  for (const field of arrayFields) {
    if (frontmatter[field] !== undefined && !Array.isArray(frontmatter[field])) {
      errors.push({
        line: 2,
        column: 1,
        message: `'${field}' should be an array`,
        severity: 'error',
        source: 'syntax',
      });
    }
  }

  return errors;
}

/**
 * Validate template variables in content
 * @param {string} content - Full content
 * @returns {Array<{ line: number, column: number, message: string, severity: string, source: string }>}
 */
function validateTemplateVariables(content) {
  const warnings = [];
  const lines = content.split('\n');

  // Check for unclosed template variables
  const unclosedPattern = /\{\{[^}]*$/;
  lines.forEach((line, index) => {
    if (unclosedPattern.test(line)) {
      warnings.push({
        line: index + 1,
        column: line.indexOf('{{') + 1,
        message: 'Unclosed template variable',
        severity: 'warning',
        source: 'syntax',
      });
    }
  });

  return warnings;
}

/**
 * Deduplicate errors by message and line
 * Prefers Pyodide (schema) errors over syntax errors
 */
function deduplicateErrors(errors) {
  const seen = new Map();

  for (const error of errors) {
    const key = `${error.line}:${error.message}`;

    if (!seen.has(key)) {
      seen.set(key, error);
    } else {
      // Prefer pyodide errors over syntax errors
      const existing = seen.get(key);
      if (error.source === 'pyodide' && existing.source !== 'pyodide') {
        seen.set(key, error);
      }
    }
  }

  return Array.from(seen.values());
}

/**
 * Hook for real-time browser-side validation with Pyodide integration
 *
 * Uses a two-phase validation approach:
 * 1. Quick JS validation for immediate feedback (syntax checks)
 * 2. Full Pydantic validation via Pyodide (schema validation)
 *
 * @param {string} content - Editor content
 * @param {string} contentType - Content type (agent, skill, prompt, judge)
 * @param {boolean} enabled - Whether validation is enabled
 * @returns {{ errors: Array, isValidating: boolean, pyodideReady: boolean, pyodideError: string|null, validate: Function }}
 */
export function useValidation(content, contentType, enabled = true) {
  const [errors, setErrors] = useState([]);
  const [isValidating, setIsValidating] = useState(false);
  const debounceRef = useRef(null);

  // Get Pyodide hook
  const {
    isReady: pyodideReady,
    isLoading: pyodideLoading,
    error: pyodideError,
    validate: pyodideValidate,
  } = usePyodide();

  /**
   * Quick synchronous validation (JavaScript only)
   */
  const quickValidate = useCallback((content) => {
    if (!content) {
      return [];
    }

    const allErrors = [];

    // Parse and validate frontmatter
    const { frontmatter, error: parseError } = parseFrontmatter(content);

    if (parseError) {
      allErrors.push(parseError);
    } else {
      // Validate frontmatter fields
      const frontmatterErrors = quickValidateFrontmatter(frontmatter, contentType);
      allErrors.push(...frontmatterErrors);
    }

    // Validate template variables
    const templateErrors = validateTemplateVariables(content);
    allErrors.push(...templateErrors);

    return allErrors;
  }, [contentType]);

  /**
   * Full validation including Pyodide
   */
  const fullValidate = useCallback(async (content) => {
    if (!content) {
      return [];
    }

    // Start with quick validation
    const quickErrors = quickValidate(content);

    // If Pyodide is ready, run full schema validation
    if (pyodideReady) {
      try {
        const pyodideErrors = await pyodideValidate(contentType, content);
        // Merge and deduplicate
        return deduplicateErrors([...quickErrors, ...pyodideErrors]);
      } catch (err) {
        console.error('Pyodide validation failed:', err);
        // Fall back to quick validation only
        return quickErrors;
      }
    }

    return quickErrors;
  }, [contentType, quickValidate, pyodideReady, pyodideValidate]);

  /**
   * Manual validation trigger
   */
  const validate = useCallback(async () => {
    if (!enabled || !content) {
      setErrors([]);
      return [];
    }

    setIsValidating(true);

    try {
      const allErrors = await fullValidate(content);
      setErrors(allErrors);
      return allErrors;
    } finally {
      setIsValidating(false);
    }
  }, [enabled, content, fullValidate]);

  // Track if we've done initial validation
  const hasValidatedRef = useRef(false);
  const lastContentRef = useRef(content);

  // Debounced validation on content change
  useEffect(() => {
    if (!enabled) {
      setErrors([]);
      hasValidatedRef.current = false;
      return;
    }

    // Clear previous timeout
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Only show quick validation immediately if we haven't validated yet
    // or if Pyodide isn't ready (to avoid flashing)
    if (content && !hasValidatedRef.current) {
      const quickErrors = quickValidate(content);
      setErrors(quickErrors);
    }

    // Debounced full validation (including Pyodide)
    debounceRef.current = setTimeout(async () => {
      if (content) {
        setIsValidating(true);
        try {
          const allErrors = await fullValidate(content);
          setErrors(allErrors);
          hasValidatedRef.current = true;
          lastContentRef.current = content;
        } finally {
          setIsValidating(false);
        }
      }
    }, VALIDATION_DEBOUNCE_MS);

    // Cleanup
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [content, contentType, enabled, quickValidate, fullValidate]);

  // Re-validate when Pyodide becomes ready (only once)
  const pyodideValidatedRef = useRef(false);
  useEffect(() => {
    if (pyodideReady && enabled && content && !pyodideValidatedRef.current) {
      // Trigger full validation now that Pyodide is ready
      pyodideValidatedRef.current = true;
      fullValidate(content).then(setErrors);
    }
  }, [pyodideReady, enabled, content, fullValidate]);

  return {
    errors,
    isValidating,
    pyodideReady,
    pyodideLoading,
    pyodideError,
    validate,
  };
}

export default useValidation;
